import { ref, watch } from 'vue';

import { useJobEditStore } from '@/stores/useJobEditStore';
import {
  fetchPostJobSetOnHold,
  fetchPostJobUnsetOnHold,
} from '@/helpers/fetchers';

interface UseJobEditContentDataOptions {
  fetchTickets: () => Promise<void>;
  fetchStopImages: () => Promise<void>;
  fetchCustomerOpsEmails: () => Promise<void>;
  fetchChannelsEmails: () => Promise<void>;
}

export function useJobEditContentData(options: UseJobEditContentDataOptions) {
  const jobEditStore = useJobEditStore();
  const initializedSideDataJobKey = ref<string | null>(null);
  const isHoldLoading = ref(false);

  watch(
    () => jobEditStore.jobData,
    (jobData) => {
      if (!jobData) {
        return;
      }

      const jobKey = `${jobData.rockhopper_id}-${jobData.rockhopper_date}`;

      if (initializedSideDataJobKey.value === jobKey) {
        return;
      }

      initializedSideDataJobKey.value = jobKey;

      void options.fetchTickets();
      void options.fetchStopImages();
      void options.fetchCustomerOpsEmails();
      void options.fetchChannelsEmails();
    },
    { immediate: true },
  );

  async function handleToggleHold(): Promise<void> {
    if (isHoldLoading.value) {
      return;
    }

    if (
      !jobEditStore.jobData?.rockhopper_id ||
      !jobEditStore.jobData.rockhopper_date
    ) {
      return;
    }

    isHoldLoading.value = true;

    const requestParams = {
      job_id: jobEditStore.jobData.rockhopper_id,
      job_date: jobEditStore.jobData.rockhopper_date,
    };

    const isOnHold =
      jobEditStore.jobData.payment_status.toUpperCase() === 'ON HOLD' ||
      jobEditStore.jobData.invoice_status.toUpperCase() === 'ON HOLD';

    try {
      if (isOnHold) {
        await fetchPostJobUnsetOnHold(requestParams);
      } else {
        await fetchPostJobSetOnHold(requestParams);
      }

      await jobEditStore.loadJob(true);
    } finally {
      isHoldLoading.value = false;
    }
  }

  return {
    isHoldLoading,
    handleToggleHold,
  };
}
